import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  Users,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  DollarSign,
  TrendingUp,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Target,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { loanStagePipelineData, monthlyTrendData } from '../../data/mockData';
import { getAdminDashboard, getCommissionRecords } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const formatCurrency = (value) => {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)} L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)} K`;
  }
  return `₹${value}`;
};

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  };

  return (
    <Card className="border border-border hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className="text-2xl font-bold mt-1 font-['Plus_Jakarta_Sans']">{value}</p>
            {trend && (
              <div className="flex items-center gap-1 mt-2">
                {trend === 'up' ? (
                  <ArrowUpRight className="h-4 w-4 text-emerald-500" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-500" />
                )}
                <span
                  className={`text-xs font-medium ${
                    trend === 'up' ? 'text-emerald-500' : 'text-red-500'
                  }`}
                >
                  {trendValue}
                </span>
                <span className="text-xs text-muted-foreground">vs last month</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#6B7280'];

const AdminDashboard = () => {
  const location = useLocation();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loanStageData, setLoanStageData] = useState(loanStagePipelineData);
  const [monthlyData, setMonthlyData] = useState(monthlyTrendData);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [loanTypeData, setLoanTypeData] = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [recentCommissionRows, setRecentCommissionRows] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const [dashboardResp, recordsResp] = await Promise.all([
          getAdminDashboard(token),
          getCommissionRecords(token),
        ]);
        if (!isMounted) return;
        const d = dashboardResp.data;
        const items = Array.isArray(recordsResp?.data?.items) ? recordsResp.data.items : [];
        setStats({
          totalLeads: d.totalLeads,
          totalFileLogin: d.totalFileLogin,
          underProcessing: d.underProcessing,
          approvedLoans: d.approvedLoans,
          rejectedLoans: d.rejectedLoans,
          totalDisbursement: d.totalDisbursement,
          totalCommissionLiability: d.totalCommissionLiability,
          totalRevenue: d.totalRevenue,
        });
        setLoanStageData(Array.isArray(d.loanStagePipeline) && d.loanStagePipeline.length ? d.loanStagePipeline : loanStagePipelineData);
        setMonthlyData(Array.isArray(d.monthlyTrend) && d.monthlyTrend.length ? d.monthlyTrend : monthlyTrendData);
        setAgentPerformance(Array.isArray(d.agentPerformance) ? d.agentPerformance : []);
        setLoanTypeData(Array.isArray(d.loanTypeDistribution) ? d.loanTypeDistribution : []);
        setCommissionData(Array.isArray(d.commissionOverview) ? d.commissionOverview : []);
        setAlerts(d.notifications || []);
        setRecentCommissionRows(items.slice(0, 6));
      } catch (e) {
        if (!isMounted) return;
        setError(e.message || 'Failed to load dashboard');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const section = useMemo(() => {
    if (location.pathname.includes('/admin/dashboard/kpi')) return 'kpi';
    if (location.pathname.includes('/admin/dashboard/commission')) return 'commission';
    if (location.pathname.includes('/admin/dashboard/performance')) return 'performance';
    if (location.pathname.includes('/admin/dashboard/alerts')) return 'alerts';
    return 'overview';
  }, [location.pathname]);

  useEffect(() => {
    const idBySection = {
      overview: 'admin-dashboard-overview',
      kpi: 'admin-dashboard-kpi',
      commission: 'admin-dashboard-commission',
      performance: 'admin-dashboard-performance',
      alerts: 'admin-dashboard-alerts',
    };

    const targetId = idBySection[section];
    if (!targetId) return;

    // defer to ensure layout is painted
    requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [section]);

  const recentAlerts = alerts.length
    ? alerts.filter((n) => !n.read).slice(0, 4)
    : [];

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const badgeClass = (status) => (
    status === 'paid'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'approved'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-amber-50 text-amber-700 border-amber-200'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  const safeStats = stats || {
    totalLeads: 0,
    totalFileLogin: 0,
    underProcessing: 0,
    approvedLoans: 0,
    rejectedLoans: 0,
    totalDisbursement: 0,
    totalCommissionLiability: 0,
    totalRevenue: 0,
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard" id="admin-dashboard-overview">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            {error ? error : "Welcome back! Here's what's happening today."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="export-report-btn">
            Export Report
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-lead-btn">
            Add New Lead
          </Button>
        </div>
      </div>

      {/* Stats Grid - Bento Layout */}
      <div id="admin-dashboard-kpi" className="scroll-mt-24 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Leads"
            value={safeStats.totalLeads}
            icon={Users}
            trend="up"
            trendValue="+12%"
            color="blue"
          />
          <StatCard
            title="File Login"
            value={safeStats.totalFileLogin}
            icon={FileText}
            trend="up"
            trendValue="+8%"
            color="emerald"
          />
          <StatCard
            title="Under Processing"
            value={safeStats.underProcessing}
            icon={Clock}
            color="amber"
          />
          <StatCard
            title="Approved Loans"
            value={safeStats.approvedLoans}
            icon={CheckCircle2}
            trend="up"
            trendValue="+15%"
            color="emerald"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Rejected Loans"
            value={safeStats.rejectedLoans}
            icon={XCircle}
            trend="down"
            trendValue="-5%"
            color="red"
          />
          <StatCard
            title="Total Disbursement"
            value={formatCurrency(safeStats.totalDisbursement)}
            icon={DollarSign}
            trend="up"
            trendValue="+22%"
            color="emerald"
          />
          <StatCard
            title="Commission Liability"
            value={formatCurrency(safeStats.totalCommissionLiability)}
            icon={Building2}
            color="purple"
          />
          <StatCard
            title="Total Revenue"
            value={formatCurrency(safeStats.totalRevenue)}
            icon={TrendingUp}
            trend="up"
            trendValue="+18%"
            color="emerald"
          />
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-['Plus_Jakarta_Sans']">Monthly Trend</CardTitle>
            <CardDescription>Leads, Disbursements & Rejections over 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorDisbursed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="leads"
                    stroke="#3B82F6"
                    fillOpacity={1}
                    fill="url(#colorLeads)"
                    strokeWidth={2}
                    name="Leads"
                  />
                  <Area
                    type="monotone"
                    dataKey="disbursed"
                    stroke="#10B981"
                    fillOpacity={1}
                    fill="url(#colorDisbursed)"
                    strokeWidth={2}
                    name="Disbursed"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Loan Stage Pipeline */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-['Plus_Jakarta_Sans']">Loan Stage Pipeline</CardTitle>
            <CardDescription>Current distribution across stages</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={loanStageData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    width={100}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="value" fill="#10B981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Agent Performance */}
        <Card id="admin-dashboard-performance" className="border border-border lg:col-span-2 scroll-mt-24">
          <CardHeader>
            <CardTitle className="font-['Plus_Jakarta_Sans']">Agent Performance</CardTitle>
            <CardDescription>Top performing agents by loan files this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {agentPerformance.map((agent, index) => (
                <div key={agent.name} className="flex items-center gap-4">
                  <div className="w-8 text-center">
                    <span
                      className={`text-sm font-bold ${
                        index === 0
                          ? 'text-amber-500'
                          : index === 1
                          ? 'text-slate-400'
                          : index === 2
                          ? 'text-amber-700'
                          : 'text-muted-foreground'
                      }`}
                    >
                      #{index + 1}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{agent.name}</span>
                      <span className="text-sm text-muted-foreground">
                        {agent.leads}/{agent.target} files
                      </span>
                    </div>
                    <Progress
                      value={(agent.leads / agent.target) * 100}
                      className="h-2"
                    />
                  </div>
                  <Badge
                    variant={agent.conversion >= 55 ? 'default' : 'secondary'}
                    className={
                      agent.conversion >= 55
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
                        : ''
                    }
                  >
                    {agent.conversion}% DR
                  </Badge>
                </div>
              ))}
              {!agentPerformance.length ? (
                <p className="text-sm text-muted-foreground">No active agent performance data available.</p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Loan Type Distribution */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="font-['Plus_Jakarta_Sans']">Loan Distribution</CardTitle>
            <CardDescription>By loan type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={loanTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {loanTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {loanTypeData.length ? (
              <div className="grid grid-cols-2 gap-2 mt-4">
                {loanTypeData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-xs text-muted-foreground">{item.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-4">No loan distribution data available.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alerts & Commission Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts & Tasks */}
        <Card id="admin-dashboard-alerts" className="border border-border scroll-mt-24">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-['Plus_Jakarta_Sans']">Alerts & Tasks</CardTitle>
              <CardDescription>Recent notifications requiring attention</CardDescription>
            </div>
            <Badge variant="destructive">{recentAlerts.length}</Badge>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex items-start gap-3 p-3 rounded-lg bg-accent/50"
                >
                  <div
                    className={`p-2 rounded-full ${
                      alert.priority === 'High'
                        ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                    }`}
                  >
                    <AlertCircle className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{alert.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {alert.message}
                    </p>
                  </div>
                  <Badge variant="outline" className="shrink-0">
                    {alert.priority}
                  </Badge>
                </div>
              ))}
              {!recentAlerts.length ? (
                <p className="text-sm text-muted-foreground">No commission or payout alerts right now.</p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Commission Overview */}
        <Card id="admin-dashboard-commission" className="border border-border scroll-mt-24">
          <CardHeader>
            <CardTitle className="font-['Plus_Jakarta_Sans']">Commission Overview</CardTitle>
            <CardDescription>Paid vs Pending commissions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={commissionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                    tickFormatter={(value) => `₹${value / 1000}K`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`₹${value.toLocaleString()}`, '']}
                  />
                  <Bar dataKey="paid" fill="#10B981" name="Paid" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pending" fill="#F59E0B" name="Pending" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {!commissionData.length ? (
              <p className="text-sm text-muted-foreground">No commission trend available yet.</p>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="font-['Plus_Jakarta_Sans']">Recent Commission Ledger</CardTitle>
          <CardDescription>Visible commission records for super admin scope only.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Loan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentCommissionRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.recipient_name || '—'}</TableCell>
                  <TableCell className="capitalize">{row.recipient_role}</TableCell>
                  <TableCell className="font-mono text-sm">{row.loan_application_id}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(Number(row.amount || 0))}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={badgeClass(row.status)}>
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(row.paid_at || row.created_at)}</TableCell>
                </TableRow>
              ))}
              {!recentCommissionRows.length ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                    No commission records found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
